import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { DownloadCloud02Icon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Spinner } from '@/components/ui/spinner'
import { InviteeUpload } from '@/features/marquee/components/invitee-upload'
import { InviteeUploadProgress } from '@/features/marquee/components/invitee-upload-progress'
import { saveCsv } from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { getInviteeImportTemplate } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const FILENAME_UTF8_RE = /filename\*=(?:UTF-8'')?([^;]+)/i
const FILENAME_PLAIN_RE = /filename="?([^";]+)"?/i
const SURROUNDING_QUOTES_RE = /^"|"$/g
const TEMPLATE_FALLBACK_FILENAME = 'marquee-invitee-import-template.csv'

/**
 * RFC 5987 (`filename*=`) first, then a plain quoted/unquoted `filename=`.
 *
 * Unlike the export downloads, this endpoint does set `content-disposition`
 * (the backend's `importTemplate` handler writes it explicitly), so the header
 * is the authoritative name here.
 */
const filenameFromDisposition = (header: unknown): string => {
	if (typeof header !== 'string') {
		return TEMPLATE_FALLBACK_FILENAME
	}
	const utf8 = header.match(FILENAME_UTF8_RE)
	if (utf8?.[1]) {
		return decodeURIComponent(
			utf8[1].replace(SURROUNDING_QUOTES_RE, '').trim()
		)
	}
	const plain = header.match(FILENAME_PLAIN_RE)
	return plain?.[1]?.trim() || TEMPLATE_FALLBACK_FILENAME
}

interface EventFormInviteeSectionProps {
	file: File | null
	onFileChange: (file: File | null) => void
	/** Shows the "required" message when the user submits without a CSV. */
	error: boolean
	/** A create submit is running: the picker is replaced by the busy card. */
	isSubmitting: boolean
	/** The create call is still open; the import has not started yet. */
	isCreating: boolean
}

/**
 * Invitee CSV import block, shown only on create (on edit the invitee set is
 * managed from the Attendees tab). The file lives outside the zod schema.
 */
export function EventFormInviteeSection({
	file,
	onFileChange,
	error,
	isSubmitting,
	isCreating
}: EventFormInviteeSectionProps) {
	const { t } = useTranslation()
	// GET /import-template: the canonical invitee CSV the MM fills in before
	// uploading.
	const downloadTemplate = useMutation({
		mutationFn: async () => {
			const response = await getInviteeImportTemplate()
			return {
				csv: response.data,
				filename: filenameFromDisposition(
					response.headers['content-disposition']
				)
			}
		},
		onSuccess: ({ csv, filename }) => saveCsv(filename, csv),
		onError: (error) => errorHandler(error)
	})
	return (
		<div className='flex flex-col gap-3'>
			<div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-1'>
				<h2 className='font-medium text-base text-neutral-900'>
					{t('marquee:invitee_section_title')}
				</h2>
				<Button
					className='shrink-0 px-0'
					disabled={downloadTemplate.isPending}
					onClick={() => downloadTemplate.mutate()}
					size='sm'
					type='button'
					variant='tertiary'
				>
					{downloadTemplate.isPending ? (
						<Spinner size={16} />
					) : (
						<DownloadCloud02Icon />
					)}
					{t('marquee:invitee_download_template')}
				</Button>
			</div>
			{isSubmitting && file ? (
				<InviteeUploadProgress
					fileName={file.name}
					isCreating={isCreating}
				/>
			) : (
				<InviteeUpload file={file} onFileChange={onFileChange} />
			)}
			<InputHelpText
				error={error ? t('marquee:error_invitee_required') : undefined}
			/>
		</div>
	)
}
